import React, { useState, useEffect } from "react";
import { Card, Row, Col, Badge, Button, Spinner, Alert } from "react-bootstrap";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  where,
  getCountFromServer,
} from "firebase/firestore";
import "./TimeshareList.css";
import { db } from "../../services/firebase";
import {
  BsHouseDoor,
  BsArrowRight,
  BsClock,
  BsCheckCircleFill,
  BsClockFill,
  BsPlayCircleFill,
  BsXCircleFill,
  BsQuestionCircleFill,
} from "react-icons/bs";

// Map pipelineStage to appropriate status
const getStatusFromPipelineStage = (pipelineStage) => {
  if (!pipelineStage) return "Unknown";

  const stage = pipelineStage.toLowerCase().trim();

  // Pending status stages
  if (
    [
      "rescission",
      "tf hold",
      "pender",
      "ihf",
      "non compliant",
      "cc dispute",
      "welcome call",
    ].includes(stage)
  ) {
    return "pending";
  }

  // Active status stages
  if (["docs requested", "vendor", "reassigned vendor"].includes(stage)) {
    return "active";
  }

  // Cancelled status stages
  if (["cancelled", "work stop"].includes(stage)) {
    return "cancelled";
  }

  // Completed status stages
  if (["completed", "closed"].includes(stage)) {
    return "completed";
  }

  // Default to original status if no mapping found
  return "Unknown";
};

// Get status description based on mapped status
const getStatusDescription = (status) => {
  switch (status?.toLowerCase()) {
    case "active":
      return "Your timeshare exit is in progress. Our team or legal partner is actively working on your case and may request additional documents if needed.";
    case "pending":
      return "Your file is awaiting payment or required documents. Once received, your case will move to Active status for processing.";
    case "completed":
      return "Your timeshare exit has been successfully completed. Congratulations on your release from all timeshare obligations!";
    case "cancelled":
      return "Your services have been cancelled and your account is now closed. Please contact us if you believe this was done in error.";
    default:
      return "Status information is not available at this time.";
  }
};

const getStatusIcon = (status) => {
  switch (status?.toLowerCase()) {
    case "active":
      return BsPlayCircleFill;
    case "pending":
      return BsClockFill;
    case "completed":
      return BsCheckCircleFill;
    case "cancelled":
      return BsXCircleFill;
    default:
      return BsQuestionCircleFill;
  }
};

const TimeshareList = ({ uid }) => {
  const [timeshares, setTimeshares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [counts, setCounts] = useState({ total: 0 });

  // Fetch counts using getCountFromServer for better performance
  const fetchCounts = async () => {
    if (!uid) return;

    try {
      const timesharesRef = collection(db, "timeshares");

      // Get total count
      const totalQuery = query(timesharesRef, where("uid", "==", uid));
      const totalSnapshot = await getCountFromServer(totalQuery);

      setCounts({
        total: totalSnapshot.data().count,
      });
    } catch (err) {
      console.error("Error fetching counts:", err);
      setCounts({ total: 0, active: 0, completed: 0 });
    }
  };

  useEffect(() => {
    if (!uid) {
      setError("User ID is required");
      setLoading(false);
      return;
    }

    // Fetch counts
    fetchCounts();

    const timesharesRef = collection(db, "timeshares");
    const q = query(
      timesharesRef,
      where("uid", "==", uid),
      orderBy("updatedAt", "desc")
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        try {
          const timeshareData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setTimeshares(timeshareData);
          setError("");
        } catch (err) {
          setError("Failed to load timeshares");
          console.error("Error loading timeshares:", err);
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        setError("Failed to connect to database");
        setLoading(false);
        console.error("Firestore error:", err);
      }
    );

    return () => unsubscribe();
  }, [uid]);

  // Loading Component
  const LoadingState = () => (
    <div className="timeshare-loading">
      <div className="loading-content">
        <Spinner animation="border" variant="primary" size="lg" />
        <h5 className="mt-3">Loading Your Timeshares...</h5>
        <p className="text-muted">Please wait while we fetch your data</p>
      </div>
    </div>
  );

  // Error Component
  const ErrorState = () => (
    <Alert variant="danger" className="timeshare-error">
      <Alert.Heading>Unable to Load Timeshares</Alert.Heading>
      <p>{error}</p>
      <Button variant="outline-danger" onClick={() => window.location.reload()}>
        Try Again
      </Button>
    </Alert>
  );

  // Empty State Component
  const EmptyState = () => (
    <div className="timeshare-empty">
      <div className="empty-content">
        <BsHouseDoor className="empty-icon" />
        <h4>No Active Timeshares</h4>
        <p className="text-muted">
          You don't have any active timeshare cancellations at the moment.
        </p>
      </div>
    </div>
  );

  if (loading) return <LoadingState />;
  if (error) return <ErrorState />;

  return (
    <div className="timeshare-container">
      {/* Header Section */}
      <div className="timeshare-header">
        <div className="header-content">
          <div className="header-left">
            <BsHouseDoor className="header-icon" />
            <div>
              <h4 className="header-title">My Timeshare Cancellations</h4>
              <p className="header-subtitle">
                Track your cancellation progress and manage your properties
              </p>
            </div>
          </div>
          <div className="header-right">
            <div className="stats-badges">
              <Badge bg="primary" pill className="stat-badge">
                {counts.total} Total
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="timeshare-content">
        {timeshares.length === 0 ? (
          <EmptyState />
        ) : (
          <Row xs={1} md={2} lg={3} className="g-4">
            {timeshares.map((timeshare) => (
              <Col key={timeshare.id}>
                <Card
                  className={`timeshare-card ${
                    timeshare.status === "completed" ? "completed" : "active"
                  }`}
                  onClick={() =>
                    (window.location.href = `/dashboard/timeshares/${timeshare.id}`)
                  }
                >
                  <Card.Body>
                    <div className="card-header-section">
                      <div className="property-info">
                        <h6 className="property-name">
                          {timeshare.opportunityName || "Timeshare Property"}
                        </h6>
                        <div className="status-section">
                          <Badge
                            style={{
                              color: "white",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              border: "none",
                            }}
                            className={`status-badge status-${getStatusFromPipelineStage(
                              timeshare.pipelineStage
                            )}`}
                          >
                            {React.createElement(
                              getStatusIcon(
                                getStatusFromPipelineStage(
                                  timeshare.pipelineStage
                                )
                              ),
                              {
                                style: { fontSize: "0.9rem" },
                              }
                            )}
                            {getStatusFromPipelineStage(
                              timeshare.pipelineStage
                            )}
                          </Badge>
                        </div>
                      </div>
                      <div className="status-description">
                        <small className="text-muted">
                          {getStatusDescription(
                            getStatusFromPipelineStage(timeshare.pipelineStage)
                          )}
                        </small>
                      </div>
                    </div>

                    <div className="card-footer-section">
                      <div className="update-date-section">
                        <small className="text-muted d-flex align-items-center">
                          <BsClock
                            className="me-1"
                            style={{ fontSize: "0.8rem" }}
                          />
                          Last Updated:{" "}
                          {timeshare.updatedAt
                            ? new Date(
                                timeshare.updatedAt.seconds * 1000
                              ).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })
                            : "Not Available"}
                        </small>
                      </div>
                      <div className="action-buttons">
                        <Button
                          size="sm"
                          variant="primary"
                          className="action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.location.href = `/dashboard/timeshares/${timeshare.id}`;
                          }}
                        >
                          <BsArrowRight className="me-1" />
                          View Details
                        </Button>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </div>

      {/* Results Info */}
      {timeshares.length > 0 && (
        <div className="results-info">
          <p className="text-muted">
            Showing {timeshares.length} timeshare cancellations
          </p>
        </div>
      )}
    </div>
  );
};

export default TimeshareList;
